/**
 * System Campaign Templates
 *
 * Pre-built email and voice campaign templates for common use cases.
 * Users can copy these templates to create their own campaigns.
 *
 * @see Issue #400 - System Templates for First-Time User Experience
 */

import type { CampaignTemplate } from './types';

// =============================================================================
// CAMPAIGN TEMPLATE IDs
// =============================================================================

export const CAMPAIGN_TEMPLATE_IDS = {
  PRODUCT_LAUNCH: 'tpl-camp-product-launch',
  MONTHLY_NEWSLETTER: 'tpl-camp-monthly-newsletter',
  RATE_ANNOUNCEMENT: 'tpl-camp-rate-announcement',
  HOLIDAY_GREETING: 'tpl-camp-holiday-greeting',
} as const;

// =============================================================================
// CAMPAIGN TEMPLATES
// =============================================================================

export const CAMPAIGN_TEMPLATES: CampaignTemplate[] = [
  // ---------------------------------------------------------------------------
  // Product Launch Campaign
  // ---------------------------------------------------------------------------
  {
    id: CAMPAIGN_TEMPLATE_IDS.PRODUCT_LAUNCH,
    name: 'Product Launch',
    description:
      'Announce new products, features, or loan programs to your audience with a professional, engaging email.',
    category: 'announcement',
    icon: 'rocket_launch',
    color: '#E91E63',
    tags: ['announcement', 'product', 'launch', 'new-feature', 'marketing'],
    complexity: 'beginner',
    industryFit: ['lending', 'fintech', 'general'],
    data: {
      campaignType: 'email',
      subject: 'Introducing: {{product_name}} - Now Available!',
      content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1 style="color: #333; font-size: 28px; margin-bottom: 20px;">
    Introducing {{product_name}}
  </h1>

  <p style="color: #666; font-size: 16px; line-height: 1.6;">
    Hi {{first_name}},
  </p>

  <p style="color: #666; font-size: 16px; line-height: 1.6;">
    We're excited to announce our newest offering: <strong>{{product_name}}</strong>!
  </p>

  <div style="background: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0;">
    <h2 style="color: #333; font-size: 20px; margin-bottom: 15px;">What's New?</h2>
    <ul style="color: #666; font-size: 16px; line-height: 1.8;">
      <li><strong>{{feature_1}}</strong></li>
      <li><strong>{{feature_2}}</strong></li>
      <li><strong>{{feature_3}}</strong></li>
    </ul>
  </div>

  <p style="color: #666; font-size: 16px; line-height: 1.6;">
    {{product_description}}
  </p>

  <div style="text-align: center; margin: 30px 0;">
    <a href="{{cta_link}}" style="background: #4CAF50; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px; font-size: 16px; font-weight: bold;">
      {{cta_text}}
    </a>
  </div>

  <p style="color: #666; font-size: 16px; line-height: 1.6;">
    Questions? Reply to this email or call us at {{phone_number}}.
  </p>

  <p style="color: #666; font-size: 16px; line-height: 1.6;">
    Best regards,<br/>
    The {{company_name}} Team
  </p>
</div>`,
      textContent:
        'Introducing {{product_name}}! We are excited to announce our newest offering. Key features: {{feature_1}}, {{feature_2}}, {{feature_3}}. Learn more at {{cta_link}}',
      fromNameSuggestion: '{{company_name}}',
      recommendedSendTime: 'Tuesday or Wednesday, 10:00 AM',
      recommendedSegment: 'All active contacts',
    },
  },

  // ---------------------------------------------------------------------------
  // Monthly Newsletter
  // ---------------------------------------------------------------------------
  {
    id: CAMPAIGN_TEMPLATE_IDS.MONTHLY_NEWSLETTER,
    name: 'Monthly Newsletter',
    description:
      'Keep your audience engaged with a monthly update featuring market insights, company news, and valuable content.',
    category: 'newsletter',
    icon: 'newspaper',
    color: '#2196F3',
    tags: ['newsletter', 'monthly', 'update', 'content', 'engagement'],
    complexity: 'intermediate',
    industryFit: ['real_estate', 'lending', 'mortgage', 'general'],
    data: {
      campaignType: 'email',
      subject: "{{month}} Market Update + What We're Seeing",
      content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1 style="color: #333; font-size: 24px; margin-bottom: 20px;">
    {{company_name}} Monthly Update
  </h1>

  <p style="color: #666; font-size: 16px; line-height: 1.6;">
    Hi {{first_name}},
  </p>

  <p style="color: #666; font-size: 16px; line-height: 1.6;">
    Here's what's been happening in the market and at {{company_name}} this month.
  </p>

  <!-- Market Update Section -->
  <div style="border-left: 4px solid #2196F3; padding-left: 20px; margin: 25px 0;">
    <h2 style="color: #333; font-size: 20px; margin-bottom: 10px;">Market Update</h2>
    <p style="color: #666; font-size: 16px; line-height: 1.6;">
      {{market_update_content}}
    </p>
  </div>

  <!-- Featured Article Section -->
  <div style="background: #f5f5f5; padding: 20px; border-radius: 8px; margin: 25px 0;">
    <h2 style="color: #333; font-size: 20px; margin-bottom: 10px;">Featured: {{article_title}}</h2>
    <p style="color: #666; font-size: 16px; line-height: 1.6;">
      {{article_preview}}
    </p>
    <a href="{{article_link}}" style="color: #2196F3; font-weight: bold;">Read More &rarr;</a>
  </div>

  <!-- Company News Section -->
  <div style="margin: 25px 0;">
    <h2 style="color: #333; font-size: 20px; margin-bottom: 15px;">Company News</h2>
    <ul style="color: #666; font-size: 16px; line-height: 1.8;">
      <li>{{news_item_1}}</li>
      <li>{{news_item_2}}</li>
      <li>{{news_item_3}}</li>
    </ul>
  </div>

  <!-- CTA Section -->
  <div style="text-align: center; margin: 30px 0; padding: 20px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 8px;">
    <p style="color: white; font-size: 18px; margin-bottom: 15px;">
      {{cta_headline}}
    </p>
    <a href="{{cta_link}}" style="background: white; color: #667eea; padding: 12px 25px; text-decoration: none; border-radius: 5px; font-size: 14px; font-weight: bold;">
      {{cta_text}}
    </a>
  </div>

  <p style="color: #666; font-size: 14px; line-height: 1.6;">
    Until next month,<br/>
    The {{company_name}} Team
  </p>
</div>`,
      textContent:
        '{{company_name}} Monthly Update: Market Update - {{market_update_content}}. Featured Article: {{article_title}}. Company News: {{news_item_1}}, {{news_item_2}}.',
      fromNameSuggestion: '{{company_name}} Team',
      recommendedSendTime: 'First Tuesday of the month, 9:00 AM',
      recommendedSegment: 'All subscribed contacts',
    },
  },

  // ---------------------------------------------------------------------------
  // Rate Announcement
  // ---------------------------------------------------------------------------
  {
    id: CAMPAIGN_TEMPLATE_IDS.RATE_ANNOUNCEMENT,
    name: 'Rate Announcement',
    description: 'Announce rate changes, special promotions, or limited-time offers to drive urgency and conversions.',
    category: 'announcement',
    icon: 'trending_down',
    color: '#4CAF50',
    tags: ['rates', 'announcement', 'promotion', 'urgency', 'conversion'],
    complexity: 'beginner',
    industryFit: ['lending', 'mortgage', 'fintech'],
    data: {
      campaignType: 'email',
      subject: 'New Rates: As Low As {{lowest_rate}}% - Limited Time',
      content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <!-- Hero Section -->
  <div style="background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%); padding: 40px 20px; text-align: center; border-radius: 8px 8px 0 0;">
    <h1 style="color: white; font-size: 32px; margin: 0;">
      Rates Just Dropped!
    </h1>
    <p style="color: rgba(255,255,255,0.9); font-size: 48px; font-weight: bold; margin: 20px 0;">
      {{lowest_rate}}%
    </p>
    <p style="color: rgba(255,255,255,0.8); font-size: 16px; margin: 0;">
      Starting rate for qualified borrowers
    </p>
  </div>

  <div style="padding: 30px 20px; background: #f9f9f9;">
    <p style="color: #666; font-size: 16px; line-height: 1.6;">
      Hi {{first_name}},
    </p>

    <p style="color: #666; font-size: 16px; line-height: 1.6;">
      Great news! We've lowered our rates across all loan products. Here's what's available:
    </p>

    <!-- Rate Table -->
    <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
      <tr style="background: #4CAF50; color: white;">
        <th style="padding: 12px; text-align: left;">Loan Type</th>
        <th style="padding: 12px; text-align: center;">Rate</th>
        <th style="padding: 12px; text-align: center;">LTV</th>
      </tr>
      <tr style="background: white;">
        <td style="padding: 12px; border-bottom: 1px solid #eee;">Bridge Loan</td>
        <td style="padding: 12px; text-align: center; border-bottom: 1px solid #eee;">{{bridge_rate}}%</td>
        <td style="padding: 12px; text-align: center; border-bottom: 1px solid #eee;">Up to 80%</td>
      </tr>
      <tr style="background: #f9f9f9;">
        <td style="padding: 12px; border-bottom: 1px solid #eee;">Fix & Flip</td>
        <td style="padding: 12px; text-align: center; border-bottom: 1px solid #eee;">{{fix_flip_rate}}%</td>
        <td style="padding: 12px; text-align: center; border-bottom: 1px solid #eee;">Up to 85%</td>
      </tr>
      <tr style="background: white;">
        <td style="padding: 12px; border-bottom: 1px solid #eee;">DSCR</td>
        <td style="padding: 12px; text-align: center; border-bottom: 1px solid #eee;">{{dscr_rate}}%</td>
        <td style="padding: 12px; text-align: center; border-bottom: 1px solid #eee;">Up to 80%</td>
      </tr>
    </table>

    <div style="background: #FFF3E0; padding: 15px; border-radius: 5px; margin: 20px 0;">
      <p style="color: #E65100; font-size: 14px; margin: 0;">
        <strong>Limited Time:</strong> These rates are valid through {{expiration_date}}. Lock in your rate today!
      </p>
    </div>

    <div style="text-align: center; margin: 30px 0;">
      <a href="{{apply_link}}" style="background: #4CAF50; color: white; padding: 15px 40px; text-decoration: none; border-radius: 5px; font-size: 18px; font-weight: bold;">
        Get My Rate
      </a>
    </div>

    <p style="color: #666; font-size: 14px; line-height: 1.6;">
      Questions? Call us at {{phone_number}} or reply to this email.
    </p>
  </div>
</div>`,
      textContent:
        'Rates just dropped! Starting at {{lowest_rate}}%. Bridge: {{bridge_rate}}%, Fix & Flip: {{fix_flip_rate}}%, DSCR: {{dscr_rate}}%. Valid through {{expiration_date}}. Apply now!',
      fromNameSuggestion: '{{company_name}}',
      recommendedSendTime: 'Tuesday, 10:00 AM',
      recommendedSegment: 'Active leads and past borrowers',
    },
  },

  // ---------------------------------------------------------------------------
  // Holiday Greeting
  // ---------------------------------------------------------------------------
  {
    id: CAMPAIGN_TEMPLATE_IDS.HOLIDAY_GREETING,
    name: 'Holiday Greeting',
    description:
      'Send warm holiday wishes to your contacts. Build relationships with a personal, non-promotional message.',
    category: 'seasonal',
    icon: 'celebration',
    color: '#FF5722',
    tags: ['holiday', 'greeting', 'seasonal', 'relationship', 'end-of-year'],
    complexity: 'beginner',
    industryFit: ['real_estate', 'lending', 'mortgage', 'general'],
    data: {
      campaignType: 'email',
      subject: 'Happy Holidays from {{company_name}}!',
      content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <!-- Holiday Header -->
  <div style="background: linear-gradient(135deg, #c62828 0%, #b71c1c 100%); padding: 40px 20px; text-align: center; border-radius: 8px 8px 0 0;">
    <h1 style="color: white; font-size: 32px; margin: 0;">
      Happy Holidays!
    </h1>
    <p style="color: rgba(255,255,255,0.9); font-size: 18px; margin-top: 10px;">
      From all of us at {{company_name}}
    </p>
  </div>

  <div style="padding: 30px 20px; background: #fafafa;">
    <p style="color: #666; font-size: 16px; line-height: 1.8;">
      Hi {{first_name}},
    </p>

    <p style="color: #666; font-size: 16px; line-height: 1.8;">
      As the year comes to a close, we wanted to take a moment to thank you for being part of the {{company_name}} family.
    </p>

    <p style="color: #666; font-size: 16px; line-height: 1.8;">
      This year, we had the privilege of helping investors like you achieve their goals, and we're grateful for the trust you've placed in us.
    </p>

    <div style="background: white; padding: 20px; border-radius: 8px; margin: 25px 0; text-align: center;">
      <p style="color: #333; font-size: 18px; font-style: italic; margin: 0;">
        "{{holiday_quote}}"
      </p>
    </div>

    <p style="color: #666; font-size: 16px; line-height: 1.8;">
      Wishing you and your loved ones a wonderful holiday season filled with joy, peace, and prosperity.
    </p>

    <p style="color: #666; font-size: 16px; line-height: 1.8;">
      Here's to an amazing {{next_year}}!
    </p>

    <p style="color: #666; font-size: 16px; line-height: 1.8;">
      Warm regards,<br/>
      <strong>The {{company_name}} Team</strong>
    </p>
  </div>

  <!-- Footer -->
  <div style="padding: 20px; background: #f0f0f0; text-align: center; border-radius: 0 0 8px 8px;">
    <p style="color: #999; font-size: 12px; margin: 0;">
      {{company_address}}
    </p>
  </div>
</div>`,
      textContent:
        'Happy Holidays from {{company_name}}! Thank you for being part of our family this year. Wishing you joy, peace, and prosperity. Here is to an amazing {{next_year}}!',
      fromNameSuggestion: 'The {{company_name}} Team',
      recommendedSendTime: 'December 23-24, morning',
      recommendedSegment: 'All contacts',
    },
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all campaign templates
 */
export function getAllCampaignTemplates(): CampaignTemplate[] {
  return CAMPAIGN_TEMPLATES;
}

/**
 * Get campaign template by ID
 */
export function getCampaignTemplateById(id: string): CampaignTemplate | undefined {
  return CAMPAIGN_TEMPLATES.find((t) => t.id === id);
}

/**
 * Get campaign templates by category
 */
export function getCampaignTemplatesByCategory(category: string): CampaignTemplate[] {
  return CAMPAIGN_TEMPLATES.filter((t) => t.category === category);
}

/**
 * Get campaign templates by type (email/voice/sms)
 */
export function getCampaignTemplatesByType(campaignType: 'email' | 'voice' | 'sms'): CampaignTemplate[] {
  return CAMPAIGN_TEMPLATES.filter((t) => t.data.campaignType === campaignType);
}

/**
 * Search campaign templates by name, description, or tags
 */
export function searchCampaignTemplates(query: string): CampaignTemplate[] {
  const lowerQuery = query.toLowerCase();
  return CAMPAIGN_TEMPLATES.filter(
    (t) =>
      t.name.toLowerCase().includes(lowerQuery) ||
      t.description.toLowerCase().includes(lowerQuery) ||
      t.tags.some((tag) => tag.toLowerCase().includes(lowerQuery)),
  );
}
